const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const getAuthHeadersMultipart = () => {
  const token = localStorage.getItem('token');
  return {
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// ─── Profile ───────────────────────────────────────────────────────────────────

export const getProfile = async () => {
  const response = await fetch(`${API_URL}/community-kitchen/profile`, {
    headers: getAuthHeaders()
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch profile');
  return response.json();
};

export const createProfile = async (profileData) => {
  const response = await fetch(`${API_URL}/community-kitchen/profile`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(profileData)
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to create profile');
  }
  return response.json();
};

export const updateProfile = async (profileData) => {
  const response = await fetch(`${API_URL}/community-kitchen/profile`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(profileData)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to update profile');
  return response.json();
};

export const getProfileById = async (id) => {
  const response = await fetch(`${API_URL}/community-kitchen/profile/${id}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch profile');
  return response.json();
};

// ─── Matches ───────────────────────────────────────────────────────────────────

export const getAvailableMatches = async (role, radiusMiles) => {
  const params = new URLSearchParams({ role });
  if (radiusMiles) params.append('radiusMiles', radiusMiles);
  const response = await fetch(`${API_URL}/community-kitchen/matches/available?${params}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to find matches');
  return response.json();
};

export const createMatch = async (matchData) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(matchData)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to create match');
  return response.json();
};

export const getMyMatches = async (status) => {
  const params = status ? `?status=${status}` : '';
  const response = await fetch(`${API_URL}/community-kitchen/matches${params}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch matches');
  return response.json();
};

export const getMatch = async (id) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${id}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch match');
  return response.json();
};

export const updateMatchLogistics = async (id, logistics) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${id}/logistics`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(logistics)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to update logistics');
  return response.json();
};

export const updateMatchStatus = async (id, status, cancelReason) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${id}/status`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status, cancelReason })
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to update match');
  return response.json();
};

export const fundMatch = async (id) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${id}/fund`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fund match');
  return response.json();
};

export const selectMenuItems = async (matchId, selectedMenuItems) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/menus`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ selectedMenuItems })
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to select menus');
  return response.json();
};

export const getShoppingList = async (matchId) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/shopping-list`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to get shopping list');
  return response.json();
};

// ─── Donations ─────────────────────────────────────────────────────────────────

export const createCheckoutSession = async (matchId, amount) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ amount })
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to create checkout session');
  return response.json();
};

export const placeGroceryOrder = async (matchId, deliveryAddress) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/place-grocery-order`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ deliveryAddress })
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to place grocery order');
  return response.json();
};

export const createDonation = async (donationData) => {
  const response = await fetch(`${API_URL}/community-kitchen/donate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(donationData)
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to create donation');
  return response.json();
};

export const confirmDonation = async (donationId) => {
  const response = await fetch(`${API_URL}/community-kitchen/donate/${donationId}/confirm`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to confirm donation');
  return response.json();
};

export const getMyDonations = async () => {
  const response = await fetch(`${API_URL}/community-kitchen/donations`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch donations');
  return response.json();
};

// ─── Documentation ─────────────────────────────────────────────────────────────

export const uploadDocumentation = async (matchId, { type, caption, photo, url }) => {
  if (type === 'external_link') {
    const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/documentation`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ type, caption, url })
    });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to upload');
    return response.json();
  }

  const formData = new FormData();
  formData.append('photo', photo);
  formData.append('type', type || 'photo');
  if (caption) formData.append('caption', caption);

  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/documentation`, {
    method: 'POST',
    headers: getAuthHeadersMultipart(),
    body: formData
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to upload');
  return response.json();
};

export const getDocumentation = async (matchId) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/documentation`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch documentation');
  return response.json();
};

// ─── Messages ──────────────────────────────────────────────────────────────────

export const sendMessage = async (matchId, body) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ body })
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to send message');
  return response.json();
};

export const getMessages = async (matchId) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/messages`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch messages');
  return response.json();
};

// ─── Open Sponsorships (Recipient Discovery) ──────────────────────────────────

export const getOpenSponsorships = async (radiusMiles) => {
  const params = radiusMiles ? `?radiusMiles=${radiusMiles}` : '';
  const response = await fetch(`${API_URL}/community-kitchen/matches/open-sponsorships${params}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to fetch open sponsorships');
  return response.json();
};

export const joinMatch = async (matchId) => {
  const response = await fetch(`${API_URL}/community-kitchen/matches/${matchId}/join`, {
    method: 'PATCH',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Failed to join match');
  return response.json();
};
